import { Button, ListGroup } from 'react-bootstrap';
import { Document, UserQuery, useDeleteDocumentMutation } from '../../generated/graphql';

import { COLORS } from '../../styles/colors';
import React from 'react';
import { StatusBadge } from '../StatusBadge';
import { formatTimestamp } from '../../utils';
import { getTeacherValue } from '../../helpers/getTeacherValue';
import routes from '../../routes/routes';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

type Documents = NonNullable<UserQuery['user']>['documents'];
interface DocumentsListProps {
    documents?: Documents;
    refetch?: any;
    moderation?: boolean;
}

const StyledListGroup = styled(ListGroup)`
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    background: ${COLORS.white};
`;

const DocumentTitle = styled.h5`
    margin: 0;
    color: ${COLORS.textPrimary};
    font-weight: 600;
    font-size: 1.1rem;
`;

const DocumentDescription = styled.div`
    color: ${COLORS.textSecondary};
    font-size: 0.875rem;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const MetaLabel = styled.div`
    font-weight: 600;
    color: ${COLORS.textSecondary};
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const MetaValue = styled.div`
    color: ${COLORS.textPrimary};
    font-size: 0.875rem;
`;

const DocumentItem = styled(ListGroup.Item)`
    display: grid;
    grid-template-columns: 2fr 1fr auto;
    gap: 1.5rem;
    padding: 1.5rem;
    border: none;
    border-bottom: 1px solid #e9ecef;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${COLORS.white};

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary});
        color: ${COLORS.white};
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        ${DocumentTitle}, ${DocumentDescription}, ${MetaLabel}, ${MetaValue} {
            color: ${COLORS.white};
        }
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

const DocumentInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const DocumentMeta = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    font-size: 0.875rem;
    align-content: start;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const MetaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const ActionsColumn = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 100px;
`;

const DeleteButton = styled(Button)`
    padding: 0.5rem 1.25rem;
    border-radius: 8px;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    white-space: nowrap;
    background: ${COLORS.dark};
    border-color: ${COLORS.dark};
    color: ${COLORS.white};

    &:hover {
        transform: translateY(-1px);
        background: ${COLORS.secondary};
        border-color: ${COLORS.secondary};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
`;

export const DocumentsList: React.FC<DocumentsListProps> = ({ documents, refetch, moderation }) => {
    const history = useHistory();
    const [deleteDocument] = useDeleteDocumentMutation();

    return (
        <StyledListGroup>
            {documents?.map((document) => (
                <DocumentItem key={document.id} onClick={() => history.push(`${routes.documentDetail}/${document.id}`)}>
                    <DocumentInfo>
                        <DocumentTitle>{document.documentType?.name}</DocumentTitle>
                        <DocumentDescription>{document.text}</DocumentDescription>
                    </DocumentInfo>
                    <DocumentMeta>
                        <MetaItem>
                            <MetaLabel>Бал</MetaLabel>
                            <MetaValue>{getTeacherValue(document.documentType?.values)}</MetaValue>
                        </MetaItem>
                        <MetaItem>
                            <MetaLabel>Дата додавання</MetaLabel>
                            <MetaValue>{formatTimestamp(document.createdAt)}</MetaValue>
                        </MetaItem>
                        {document.createdAt !== document.updatedAt && (
                            <MetaItem>
                                <MetaLabel>Дата редагування</MetaLabel>
                                <MetaValue>{formatTimestamp(document.updatedAt)}</MetaValue>
                            </MetaItem>
                        )}
                        <MetaItem>
                            <MetaLabel>Статус</MetaLabel>
                            <MetaValue>
                                <StatusBadge status={document.status} />
                            </MetaValue>
                        </MetaItem>
                    </DocumentMeta>
                    {!moderation && (
                        <ActionsColumn>
                            <DeleteButton
                                variant="danger"
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                    event.stopPropagation();
                                    if (global.confirm('Ви дійсно хочете видалити документ?')) {
                                        deleteDocument({ variables: { id: document.id } });
                                        refetch && refetch();
                                    }
                                }}
                            >
                                Видалити
                            </DeleteButton>
                        </ActionsColumn>
                    )}
                </DocumentItem>
            ))}
        </StyledListGroup>
    );
};
